import type { ThemeMode } from './tokens.ts'

export type ThemePreference = ThemeMode | 'system'

/** Browser-local, shared by every account using this origin. */
export const THEME_STORAGE_KEY = 'alveary.theme.preference'
export const SYSTEM_THEME_QUERY = '(prefers-color-scheme: dark)'

export function themePreference(value: unknown): ThemePreference {
  return value === 'light' || value === 'system' ? value : 'dark'
}

export function resolveTheme(
  preference: ThemePreference,
  systemDark: boolean
): ThemeMode {
  return preference === 'system' ? (systemDark ? 'dark' : 'light') : preference
}

export function readThemePreference(): ThemePreference {
  try {
    return themePreference(window.localStorage.getItem(THEME_STORAGE_KEY))
  } catch {
    return 'dark'
  }
}

let switchVersion = 0

export function applyTheme(mode: ThemeMode): void {
  // Snap theme colours together; independent colour transitions would fade
  // foreground/background through an unreadable mid-grey during a mode switch.
  const root = document.documentElement
  const previous = root.dataset.theme
  if (previous && previous !== mode) {
    const version = ++switchVersion
    root.dataset.themeSwitching = ''
    window.requestAnimationFrame(() =>
      window.requestAnimationFrame(() => {
        if (version === switchVersion) delete root.dataset.themeSwitching
      })
    )
  }
  document.documentElement.dataset.theme = mode
  document.documentElement.style.colorScheme = mode
}

/** Also emitted as a blocking classic script before the stylesheet and auth. */
export function initializeTheme(): void {
  let systemDark = true
  try {
    systemDark = window.matchMedia(SYSTEM_THEME_QUERY).matches
  } catch {
    // Missing device API falls back to Dark for System, too.
  }
  applyTheme(resolveTheme(readThemePreference(), systemDark))
}
